import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, List, ListItem, ListItemButton, ListItemIcon, ListItemText, TextField, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import apiClient from '../api/client';

const isImageKey = (key) => /\.(png|jpe?g|gif|webp|svg)$/i.test(key || '');

const normalizePrefix = (prefix) => {
  if (!prefix) return '';
  const p = prefix.replace(/^\/+|\/+$/g, '');
  return p ? p + '/' : '';
};

const StoragePickerDialog = ({ open, onClose, onSelect, imagesOnly = false, initialPrefix = '' }) => {
  const [prefix, setPrefix] = useState(normalizePrefix(initialPrefix));
  const [tree, setTree] = useState({ folders: [], files: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [view, setView] = useState(imagesOnly ? 'grid' : 'list');
  const [filter, setFilter] = useState('');
  const [urlCache, setUrlCache] = useState({}); // key -> presigned URL
  const [selectedKey, setSelectedKey] = useState('');
  const [creating, setCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const fetchTree = async (pfx) => {
    try {
      setLoading(true);
      setError('');
      const resp = await apiClient.get('/storage/files', { params: { prefix: normalizePrefix(pfx) } });
      const data = resp.data?.data || { folders: [], files: [] };
      setTree(data);
    } catch (e) {
      console.error('Ошибка загрузки хранилища', e);
      setError('Не удалось загрузить хранилище');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchTree(prefix);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefix]);

  const goInto = (name) => {
    setPrefix(prev => normalizePrefix(prev + name));
  };

  const goBack = () => {
    if (!prefix) return;
    const parts = prefix.replace(/\/$/, '').split('/');
    parts.pop();
    setPrefix(normalizePrefix(parts.join('/')));
  };

  const triggerUpload = () => {
    try { fileInputRef.current && fileInputRef.current.click(); } catch (_) { }
  };

  const onFileChosen = async (e) => {
    const files = Array.from(e?.target?.files || []);
    if (!files.length) return;
    if (imagesOnly && files.some(f => !/^image\//i.test(f.type))) {
      setError('Можно загружать только изображения');
      return;
    }
    try {
      setUploading(true);
      setError('');
      const uploadedKeys = [];
      for (const file of files) {
        const form = new FormData();
        form.append('file', file);
        form.append('prefix', prefix);
        // eslint-disable-next-line no-await-in-loop
        const resp = await apiClient.post('/storage/files', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        const key = resp?.data?.key;
        if (key) uploadedKeys.push(key);
      }
      await fetchTree(prefix);
      if (uploadedKeys.length === 1 && onSelect) {
        onSelect(uploadedKeys[0]);
      } else if (uploadedKeys.length > 1) {
        setSelectedKey(uploadedKeys[0]);
      }
    } catch (err) {
      setError('Не удалось загрузить файлы');
    } finally {
      setUploading(false);
      try { e.target.value = ''; } catch (_) { }
    }
  };

  const visibleFiles = imagesOnly ? (tree.files || []).filter(f => isImageKey(f.key)) : (tree.files || []);
  const filteredFiles = (visibleFiles || []).filter(f => !filter || f.key.toLowerCase().includes(filter.toLowerCase()));

  useEffect(() => {
    // Предзагрузка presigned URL для изображений (только первые 60)
    const prefetch = async () => {
      if (!imagesOnly) return;
      const toFetch = filteredFiles.slice(0, 60).map(f => f.key).filter(k => !urlCache[k]);
      if (toFetch.length === 0) return;
      const entries = await Promise.all(toFetch.map(async (key) => {
        try {
          const resp = await apiClient.get(`/storage/files/${encodeURIComponent(key)}/url`);
          return [key, resp?.data?.url || ''];
        } catch (_) {
          return [key, ''];
        }
      }));
      setUrlCache(prev => ({ ...prev, ...Object.fromEntries(entries) }));
    };
    prefetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesOnly, filteredFiles.map(f => f.key).join('|')]);

  const ensurePresigned = async (key) => {
    if (urlCache[key]) return urlCache[key];
    try {
      const resp = await apiClient.get(`/storage/files/${encodeURIComponent(key)}/url`);
      const url = resp?.data?.url || '';
      setUrlCache(prev => ({ ...prev, [key]: url }));
      return url;
    } catch (_) {
      return '';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Выбрать файл из хранилища</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
          <IconButton onClick={goBack} disabled={!prefix} size="small"><ArrowBackIcon /></IconButton>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>/ {prefix || ''}</Typography>
          <Box sx={{ flex: 1 }} />
          <TextField
            size="small"
            placeholder="Поиск"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 200 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField size="small" placeholder="Новая папка" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} sx={{ width: 180 }} />
            <Button size="small" variant="outlined" onClick={async () => { await (async () => { if (!newFolderName || /[\\:*?"<>|]/.test(newFolderName)) { setError('Введите корректное имя папки'); return; } try { setCreating(true); setError(''); await apiClient.post('/storage/folders', { prefix, name: newFolderName }); setNewFolderName(''); await fetchTree(prefix); } catch (err) { setError('Не удалось создать папку'); } finally { setCreating(false); } })(); }} disabled={creating || !newFolderName.trim()}>Создать</Button>
          </Box>
          <Tooltip title={view === 'grid' ? 'Сетка' : 'Список'}>
            <IconButton onClick={() => setView(v => (v === 'grid' ? 'list' : 'grid'))} size="small">
              {view === 'grid' ? <ViewModuleIcon /> : <ViewListIcon />}
            </IconButton>
          </Tooltip>
          <input ref={fileInputRef} type="file" accept={imagesOnly ? 'image/*' : '*/*'} style={{ display: 'none' }} onChange={onFileChosen} multiple />
          <Button size="small" variant="contained" onClick={triggerUpload} disabled={uploading}>
            {uploading ? 'Загрузка…' : 'Загрузить'}
          </Button>
        </Box>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        )}
        {loading ? (
          <Typography>Загрузка...</Typography>
        ) : (
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Папки</Typography>
              <List dense sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1 }}>
                {(tree.folders || []).map(folder => (
                  <ListItem key={folder.key} disablePadding>
                    <ListItemButton onClick={() => goInto(folder.name)}>
                      <ListItemIcon><FolderIcon /></ListItemIcon>
                      <ListItemText primary={folder.name} />
                    </ListItemButton>
                  </ListItem>
                ))}
                {(!tree.folders || tree.folders.length === 0) && (
                  <ListItem><ListItemText primary="Нет папок" /></ListItem>
                )}
              </List>
            </Box>
            <Box sx={{ flex: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Файлы{imagesOnly ? ' (только изображения)' : ''}</Typography>
              {view === 'grid' && imagesOnly ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.5, maxHeight: 300, overflow: 'auto', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1, p: 1 }}>
                  {filteredFiles.map(file => (
                    <Box key={file.key} sx={{ border: `2px solid ${selectedKey === file.key ? '#1976d2' : 'rgba(255,255,255,0.15)'}`, borderRadius: 1, overflow: 'hidden', cursor: 'pointer' }} onDoubleClick={() => onSelect && onSelect(file.key)} onClick={() => setSelectedKey(file.key)}>
                      <Box sx={{ aspectRatio: '1/1', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {urlCache[file.key] ? (
                          <img src={urlCache[file.key]} alt={file.key} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <InsertDriveFileIcon />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ display: 'block', p: 0.5, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{file.key.replace(/^.+\//, '')}</Typography>
                    </Box>
                  ))}
                  {filteredFiles.length === 0 && (
                    <Typography sx={{ p: 2 }}>Нет файлов</Typography>
                  )}
                </Box>
              ) : (
                <List dense sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1 }}>
                  {filteredFiles.map(file => (
                    <ListItem key={file.key} secondaryAction={
                      <Button size="small" variant="outlined" onClick={() => onSelect && onSelect(file.key)}>
                        Выбрать
                      </Button>
                    }>
                      <ListItemIcon><InsertDriveFileIcon /></ListItemIcon>
                      <ListItemText onClick={() => setSelectedKey(file.key)} primary={file.key.replace(/^.+\//, '')} secondary={file.key} />
                    </ListItem>
                  ))}
                  {filteredFiles.length === 0 && (
                    <ListItem><ListItemText primary="Нет файлов" /></ListItem>
                  )}
                </List>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
        <Button variant="contained" disabled={!selectedKey} onClick={() => onSelect && onSelect(selectedKey)}>Выбрать</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StoragePickerDialog;

